import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonVideoPreview } from 'components/common/video/preview';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { VideoChangedFn } from 'interfaces/function-types';
import { IVideoSuggestionsRequest } from 'interfaces/i-video-options';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { DEFAULT_DELIVERY } from 'lib_ts/enums/pitches.enums';
import { useContext, useEffect, useMemo } from 'react';
import useHittersStore from 'stores/use-hitters-store';
import useListLibraryStore from 'stores/use-list-library-store';
import useMiscStore from 'stores/use-misc-store';

export const PLSidebarVideoControls = (props: {
  onVideoChanged: VideoChangedFn;
}) => {
  const deliveryDict = useMiscStore((s) => s.deliveryDict);

  const lists = useListLibraryStore((s) => s.lists);

  const activeHitter = useHittersStore((s) => s.activeHitter);

  const { pitchBP } = useContext(AimingContext);
  const { resetMSHash } = useContext(MachineContext);

  const delivery = useMemo(
    () => (pitchBP ? deliveryDict[pitchBP._id] : undefined) ?? DEFAULT_DELIVERY,
    [pitchBP, deliveryDict]
  );

  // reset hash => require resend to machine for diff video
  useEffect(() => {
    resetMSHash();
  }, [delivery]);

  const suggestionsRequest = useMemo(() => {
    if (!pitchBP) {
      // no pitch => no matches
      return undefined;
    }

    const list = lists.find((l) => l._id === pitchBP._parent_id);

    if (!list) {
      // no list => no matches
      return undefined;
    }

    const output: IVideoSuggestionsRequest = {
      list: list,
      pitch: pitchBP,
      hitter: activeHitter,
      delivery: undefined, // this will be overwritten by the preview component
    };

    return output;
  }, [lists, pitchBP, activeHitter]);

  const videoID = useMemo(
    () =>
      pitchBP
        ? VideoHelper.getPitchDeliveryVideoID(pitchBP, delivery)
        : undefined,
    [pitchBP, delivery]
  );

  return (
    <ErrorBoundary identifier="PLSidebarVideoControls">
      <CommonVideoPreview
        identifier="PLSidebarVideoPreview"
        previewPx={pitchBP?.bs.px}
        previewPz={pitchBP?.bs.pz}
        videoID={videoID}
        disabled={!pitchBP}
        onDisabled={() => {
          NotifyHelper.info({
            message_md: t('common.please-select-a-pitch-first'),
          });
        }}
        suggestionsRequest={suggestionsRequest}
        selectConfig={{
          releasePx: pitchBP?.bs.px ?? 0,
          onChange: (videoID) => {
            // parent needs to know to:
            // - validate the selection
            // - update the record in db
            // - update pitchBP
            props.onVideoChanged(videoID);
          },
        }}
        delivery={delivery}
        onChangeDelivery={(v) => {
          if (!pitchBP) {
            return;
          }

          useMiscStore.getState().setDelivery(pitchBP._id, v);
        }}
      />
    </ErrorBoundary>
  );
};
